import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getNewsDetail, getFileUrl, deleteNews, trackVisit } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ShareButtons from '../components/ShareButtons';
import DOMPurify from 'dompurify';
import { ArrowLeft, Clock, User, Edit, Trash2, Loader2, Play, Mail } from 'lucide-react';
import { toast } from 'sonner';

const fmt = d => new Date(d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const media = u => { if (!u) return null; return u.startsWith('http') && !u.includes('/api/files/') ? u : getFileUrl(u); };

// Treat content as HTML if it contains any HTML tag
const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][^>]*>/i.test(s);

export default function NewsDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async () => {
    try { const { data } = await getNewsDetail(id); setNews(data); trackVisit(`/news/${id}`); }
    catch { toast.error('No encontrada'); nav('/'); }
    finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const del = async () => {
    if (!window.confirm('¿Eliminar?')) return;
    try { await deleteNews(id); toast.success('Eliminada'); nav('/dashboard'); }
    catch { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" /></div>;
  if (!news) return null;

  const imgSrc = media(news.image_url);
  const vidSrc = media(news.video_url);
  const url = window.location.href;
  const canEdit = user && (user._id === news.author_id || user.role === 'admin');
  const gmailUrl = `https://mail.google.com/mail/?view=cm&su=${encodeURIComponent(news.title)}&body=${encodeURIComponent(news.title + '\n\n' + url)}`;

  return (
    <div data-testid="news-detail-page" className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 pb-2">
        <Link to="/" data-testid="back-to-home" className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase transition-colors">
          <ArrowLeft className="h-3 w-3" /> INICIO
        </Link>
      </div>

      {imgSrc && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mb-6">
          <div className="border-2 border-[#0B1F40] border-b-4 border-b-[#E50B15] overflow-hidden brutal-shadow">
            <img src={imgSrc} alt={news.title} className="w-full max-h-[55vh] object-cover" />
          </div>
        </div>
      )}

      <article className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <div className="mb-6">
          <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">{news.category}</span>
          <h1 data-testid="news-title" className="text-3xl sm:text-4xl lg:text-5xl text-[#0B1F40] tracking-tighter leading-none mt-2 mb-4" style={{ fontFamily: 'Archivo Black' }}>{news.title}</h1>
          <div className="flex items-center gap-3 text-xs text-[#0B1F40]/50 pb-5 border-b-4 border-[#0B1F40]">
            <span className="flex items-center gap-1 font-bold text-[#0B1F40]"><User className="h-3.5 w-3.5" />{news.author_name}</span>
            <span className="w-1.5 h-1.5 bg-[#E50B15]" />
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmt(news.created_at)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-2">
            <ShareButtons title={news.title} url={url} />
            <a href={gmailUrl} target="_blank" rel="noopener noreferrer">
              <button data-testid="share-gmail-prominent" className="bg-[#EA4335] text-white text-[10px] font-black h-9 px-5 flex items-center gap-1.5 brutal-btn-press transition-all" style={{ boxShadow: '3px 3px 0px #0B1F40' }}>
                <Mail className="h-3.5 w-3.5" /> ENVIAR POR GMAIL
              </button>
            </a>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Link to={`/edit-news/${news.id}`}>
                <button data-testid="edit-news-btn" className="bg-[#0B1F40] text-white text-[10px] font-black h-9 px-4 flex items-center gap-1.5 brutal-btn-press transition-all" style={{ boxShadow: '3px 3px 0px #E50B15' }}>
                  <Edit className="h-3.5 w-3.5" /> EDITAR
                </button>
              </Link>
              <button data-testid="delete-news-btn" onClick={del} className="bg-[#E50B15] text-white text-[10px] font-black h-9 px-4 flex items-center gap-1.5 brutal-btn-press transition-all" style={{ boxShadow: '3px 3px 0px #0B1F40' }}>
                <Trash2 className="h-3.5 w-3.5" /> ELIMINAR
              </button>
            </div>
          )}
        </div>

        {looksLikeHtml(news.content) ? (
          <div data-testid="news-content"
            className="prose prose-sm sm:prose-base max-w-none text-[#0B1F40]/80 prose-headings:text-[#0B1F40] prose-headings:font-black prose-a:text-[#E50B15] prose-blockquote:border-l-4 prose-blockquote:border-[#E50B15] prose-img:border prose-img:border-[#0B1F40]/20"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(news.content, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'rel'] }) }} />
        ) : (
          <div data-testid="news-content" className="text-base leading-relaxed text-[#0B1F40]/70 whitespace-pre-wrap">{news.content}</div>
        )}

        {Array.isArray(news.sources) && news.sources.length > 0 && (
          <div data-testid="news-sources" className="mt-8 border-t-2 border-[#0B1F40]/10 pt-4">
            <p className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0B1F40]/40 mb-2">FUENTES CONSULTADAS</p>
            <div className="flex flex-wrap gap-2">
              {news.sources.map((s, i) => (
                <span key={i} className="text-[10px] font-bold text-[#0B1F40] bg-[#F4F5F7] border border-[#0B1F40]/10 px-2 py-1">{s}</span>
              ))}
            </div>
          </div>
        )}

        {vidSrc && (
          <div className="mt-8 border-2 border-[#0B1F40] brutal-shadow overflow-hidden">
            <div className="bg-[#0B1F40] px-4 py-2 flex items-center gap-2">
              <Play className="h-3.5 w-3.5 text-[#E50B15]" />
              <span className="text-[10px] font-black tracking-[0.2em] uppercase text-white/60">VIDEO</span>
            </div>
            <video data-testid="news-video" controls className="w-full max-h-[60vh] bg-black" src={vidSrc} />
          </div>
        )}
      </article>
    </div>
  );
}
